import React, { useEffect, useRef, useState } from 'react';
import { Player, ShareSelectionRequest } from '../Model';
import { restClient } from '../RestClient';

interface ShareSelectionModalProps {
  selectionId: number;
  selectionName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const ShareSelectionModal: React.FC<ShareSelectionModalProps> = ({
  selectionId,
  selectionName,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [sharedName, setSharedName] = useState(selectionName);
  const [canEdit, setCanEdit] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareType, setShareType] = useState<'Team' | 'Player'>('Team');
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up close timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Fetch players when switching to Player share type
  useEffect(() => {
    if (shareType === 'Player' && players.length === 0) {
      setIsLoadingPlayers(true);
      restClient
        .getSelectionData(selectionId)
        .then(result => {
          if (result.IsSuccess) {
            const activePlayers = result.Value.players.filter(p => !p.isArchived);
            setPlayers(activePlayers);
          } else {
            setError('Failed to load players. Please try again.');
          }
        })
        .catch(() => {
          setError('Failed to load players. Please try again.');
        })
        .finally(() => {
          setIsLoadingPlayers(false);
        });
    }
  }, [shareType, selectionId, players.length]);

  // Update shared name when selecting a player
  useEffect(() => {
    if (shareType === 'Player' && selectedPlayerId) {
      const player = players.find(p => p.id === selectedPlayerId);
      if (player) {
        setSharedName(`${selectionName} - ${player.name}`);
      }
    } else if (shareType === 'Team') {
      setSharedName(selectionName);
    }
  }, [shareType, selectedPlayerId, players, selectionName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const request: ShareSelectionRequest = {
      email: email.trim(),
      sharedName: sharedName.trim(),
      canEdit: shareType === 'Player' ? false : canEdit,
      canShare: shareType === 'Player' ? false : canShare,
      shareType,
      playerId: shareType === 'Player' ? selectedPlayerId ?? undefined : undefined,
    };

    const result = await restClient.shareSelection(selectionId, request);

    if (result.IsSuccess) {
      if (result.Value.invitationSent) {
        setSuccessMessage(`Invitation sent to ${email.trim()}. They'll receive an email to create their account.`);
        onSuccess();
        closeTimerRef.current = setTimeout(() => onClose(), 3000);
      } else {
        onSuccess();
        onClose();
      }
    } else {
      if (result.Error.code === 'INVITATION_ALREADY_PENDING') {
        setError('An invitation has already been sent to this email for this selection.');
      } else {
        setError(result.Error.description);
      }
    }

    setIsSubmitting(false);
  };

  const isFormValid =
    email.trim() !== '' &&
    sharedName.trim() !== '' &&
    (shareType === 'Team' || selectedPlayerId !== null);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        className="modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <h2 id="share-modal-title" className="modal-title">Share Selection</h2>
          <button onClick={onClose} className="btn-close btn-close-lg" aria-label="Close">
            &times;
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Success display */}
            {successMessage && (
              <div className="alert alert-success mb-4">
                {successMessage}
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="alert alert-error mb-4">
                {error}
              </div>
            )}

            {/* Share Type Toggle */}
            <div className="form-group">
              <label className="form-label">Share Type</label>
              <div className="segmented-control">
                <button
                  type="button"
                  onClick={() => setShareType('Team')}
                  className={`segmented-control-btn ${shareType === 'Team' ? 'segmented-control-btn-active' : ''}`}
                  disabled={isSubmitting}
                >
                  Team
                </button>
                <button
                  type="button"
                  onClick={() => setShareType('Player')}
                  className={`segmented-control-btn ${shareType === 'Player' ? 'segmented-control-btn-active' : ''}`}
                  disabled={isSubmitting}
                >
                  Player
                </button>
              </div>
              <div className="form-help">
                {shareType === 'Team'
                  ? 'Share the full team selection view.'
                  : 'Share a read-only view for a specific player (e.g. for a parent).'}
              </div>
            </div>

            {/* Player picker (only for Player type) */}
            {shareType === 'Player' && (
              <div className="form-group">
                <label htmlFor="share-player" className="form-label form-label-required">
                  Player
                </label>
                {isLoadingPlayers ? (
                  <div className="text-muted text-sm">Loading players...</div>
                ) : (
                  <select
                    id="share-player"
                    value={selectedPlayerId ?? ''}
                    onChange={(e) => setSelectedPlayerId(e.target.value ? Number(e.target.value) : null)}
                    className="form-select"
                    disabled={isSubmitting}
                  >
                    <option value="">Select a player...</option>
                    {players.map(player => (
                      <option key={player.id} value={player.id}>
                        {player.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Email input */}
            <div className="form-group">
              <label htmlFor="share-email" className="form-label form-label-required">
                User Email
              </label>
              <input
                id="share-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control"
                placeholder="Enter user's email address"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Share name input (only for Team type - Player type auto-generates) */}
            {shareType === 'Team' && (
              <div className="form-group">
                <label htmlFor="share-name" className="form-label form-label-required">
                  Share Name
                </label>
                <input
                  id="share-name"
                  type="text"
                  value={sharedName}
                  onChange={(e) => setSharedName(e.target.value)}
                  className="form-control"
                  placeholder="Name shown to the recipient"
                  required
                  disabled={isSubmitting}
                />
                <div className="form-help">
                  This is the name the recipient will see in their selection list.
                </div>
              </div>
            )}

            {/* Permissions (only for Team type) */}
            {shareType === 'Team' && (
              <div className="form-group">
                <label className="form-label">Permissions</label>
                <div className="form-check mb-2">
                  <input
                    id="can-edit"
                    type="checkbox"
                    checked={canEdit}
                    onChange={(e) => setCanEdit(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="can-edit" className="form-check-label">
                    Can Edit
                  </label>
                </div>
                <div className="form-check">
                  <input
                    id="can-share"
                    type="checkbox"
                    checked={canShare}
                    onChange={(e) => setCanShare(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="can-share" className="form-check-label">
                    Can Share
                  </label>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="btn btn-primary"
          >
            {isSubmitting ? 'Sharing...' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );
};
